import type { FinancialMetricMode } from 'partner360/entities/financialMetricMode'
import type { FinData } from 'partner360/entities/pbmDashboard'
import { formatFinancialNumber } from 'partner360/lib/metricFormatting'

import type { PlanningCell, SobPlanningTableModel, SobQuarterValues } from '../model/types'

import { formatPercent, formatPercentChange } from './formatters'

const EMPTY = '—'

const formatNumber = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) {
    return EMPTY
  }

  return formatFinancialNumber(value)
}

const sumBy = (rows: FinData[], getter: (row: FinData) => number | null | undefined) => {
  let hasValue = false
  let sum = 0

  rows.forEach((row) => {
    const value = getter(row)
    if (typeof value === 'number' && Number.isFinite(value)) {
      sum += value
      hasValue = true
    }
  })

  return hasValue ? sum : null
}

const averageBy = (rows: FinData[], getter: (row: FinData) => number | null | undefined) => {
  let count = 0
  let sum = 0

  rows.forEach((row) => {
    const value = getter(row)
    if (typeof value === 'number' && Number.isFinite(value)) {
      sum += value
      count += 1
    }
  })

  return count > 0 ? sum / count : null
}

const getGrowth = (current: number | null, base: number | null) => {
  if (current === null || base === null || base === 0) {
    return null
  }

  return ((current - base) / base) * 100
}

const getAchievement = (actual: number | null, planned: number | null) => {
  if (actual === null || planned === null || planned === 0) {
    return null
  }

  return (actual / planned) * 100
}

const formatGrowth = (value: number | null) => (value === null ? EMPTY : formatPercentChange(value))
const formatAchievement = (value: number | null) => (value === null ? EMPTY : formatPercent(value))

const buildPlanningCell = (
  actual: number | null,
  planned: number | null,
  actualBase: number | null,
  planVsBase: number | null
): PlanningCell => {
  const actualYoYValue = getGrowth(actual, actualBase)
  const planVsLyYoYValue = getGrowth(planned, planVsBase)
  const achievementValue = getAchievement(actual, planned)

  return {
    actual: formatNumber(actual),
    planned: formatNumber(planned),
    actualYoY: formatGrowth(actualYoYValue),
    planVsLyYoY: formatGrowth(planVsLyYoYValue),
    achievement: formatAchievement(achievementValue),
    actualYoYValue,
    planVsLyYoYValue,
    achievementValue,
  }
}

const getMetricGetters = (mode: FinancialMetricMode) => {
  if (mode === 'ndp') {
    return {
      pyTotalActual: (row: FinData) => row.ActualsPYNdp,
      pyTotalPlan: (row: FinData) => row.PlansPYNdp,
      pyQ1Actual: (row: FinData) => row.ActualsPYQ1Ndp,
      pyQ2Actual: (row: FinData) => row.ActualsPYQ2Ndp,
      pyQ3Actual: (row: FinData) => row.ActualsPYQ3Ndp,
      pyQ4Actual: (row: FinData) => row.ActualsPYQ4Ndp,
      pyQ1Plan: (row: FinData) => row.PlansPYQ1Ndp,
      pyQ2Plan: (row: FinData) => row.PlansPYQ2Ndp,
      pyQ3Plan: (row: FinData) => row.PlansPYQ3Ndp,
      pyQ4Plan: (row: FinData) => row.PlansPYQ4Ndp,
      cyTotalActual: (row: FinData) => row.ActualsCYNdp,
      cyTotalPlan: (row: FinData) => row.PlansCYNdp,
      cyQ1Actual: (row: FinData) => row.ActualsCYQ1Ndp,
      cyQ2Actual: (row: FinData) => row.ActualsCYQ2Ndp,
      cyQ3Actual: (row: FinData) => row.ActualsCYQ3Ndp,
      cyQ4Actual: (row: FinData) => row.ActualsCYQ4Ndp,
      cyQ1Plan: (row: FinData) => row.PlansCYQ1Ndp,
      cyQ2Plan: (row: FinData) => row.PlansCYQ2Ndp,
      cyQ3Plan: (row: FinData) => row.PlansCYQ3Ndp,
      cyQ4Plan: (row: FinData) => row.PlansCYQ4Ndp,
    }
  }

  if (mode === 'tnet') {
    return {
      pyTotalActual: (row: FinData) => row.ActualsPYTNet,
      pyTotalPlan: (row: FinData) => row.PlansPYTNet,
      pyQ1Actual: (row: FinData) => row.ActualsPYQ1TNet,
      pyQ2Actual: (row: FinData) => row.ActualsPYQ2TNet,
      pyQ3Actual: (row: FinData) => row.ActualsPYQ3TNet,
      pyQ4Actual: (row: FinData) => row.ActualsPYQ4TNet,
      pyQ1Plan: (row: FinData) => row.PlansPYQ1TNet,
      pyQ2Plan: (row: FinData) => row.PlansPYQ2TNet,
      pyQ3Plan: (row: FinData) => row.PlansPYQ3TNet,
      pyQ4Plan: (row: FinData) => row.PlansPYQ4TNet,
      cyTotalActual: (row: FinData) => row.ActualsCYTNet,
      cyTotalPlan: (row: FinData) => row.PlansCYTNet,
      cyQ1Actual: (row: FinData) => row.ActualsCYQ1TNet,
      cyQ2Actual: (row: FinData) => row.ActualsCYQ2TNet,
      cyQ3Actual: (row: FinData) => row.ActualsCYQ3TNet,
      cyQ4Actual: (row: FinData) => row.ActualsCYQ4TNet,
      cyQ1Plan: (row: FinData) => row.PlansCYQ1TNet,
      cyQ2Plan: (row: FinData) => row.PlansCYQ2TNet,
      cyQ3Plan: (row: FinData) => row.PlansCYQ3TNet,
      cyQ4Plan: (row: FinData) => row.PlansCYQ4TNet,
    }
  }

  return {
    pyTotalActual: (row: FinData) => row.ActualsPYNet,
    pyTotalPlan: (row: FinData) => row.PlansPYNet,
    pyQ1Actual: (row: FinData) => row.ActualsPYQ1Net,
    pyQ2Actual: (row: FinData) => row.ActualsPYQ2Net,
    pyQ3Actual: (row: FinData) => row.ActualsPYQ3Net,
    pyQ4Actual: (row: FinData) => row.ActualsPYQ4Net,
    pyQ1Plan: (row: FinData) => row.PlansPYQ1Net,
    pyQ2Plan: (row: FinData) => row.PlansPYQ2Net,
    pyQ3Plan: (row: FinData) => row.PlansPYQ3Net,
    pyQ4Plan: (row: FinData) => row.PlansPYQ4Net,
    cyTotalActual: (row: FinData) => row.ActualsCYNet,
    cyTotalPlan: (row: FinData) => row.PlansCYNet,
    cyQ1Actual: (row: FinData) => row.ActualsCYQ1Net,
    cyQ2Actual: (row: FinData) => row.ActualsCYQ2Net,
    cyQ3Actual: (row: FinData) => row.ActualsCYQ3Net,
    cyQ4Actual: (row: FinData) => row.ActualsCYQ4Net,
    cyQ1Plan: (row: FinData) => row.PlansCYQ1Net,
    cyQ2Plan: (row: FinData) => row.PlansCYQ2Net,
    cyQ3Plan: (row: FinData) => row.PlansCYQ3Net,
    cyQ4Plan: (row: FinData) => row.PlansCYQ4Net,
  }
}

const getTotals = (rows: FinData[], metricMode: FinancialMetricMode) => {
  const getter = getMetricGetters(metricMode)

  const py = {
    totalActual: sumBy(rows, getter.pyTotalActual),
    totalPlan: sumBy(rows, getter.pyTotalPlan),
    q1Actual: sumBy(rows, getter.pyQ1Actual),
    q2Actual: sumBy(rows, getter.pyQ2Actual),
    q3Actual: sumBy(rows, getter.pyQ3Actual),
    q4Actual: sumBy(rows, getter.pyQ4Actual),
    q1Plan: sumBy(rows, getter.pyQ1Plan),
    q2Plan: sumBy(rows, getter.pyQ2Plan),
    q3Plan: sumBy(rows, getter.pyQ3Plan),
    q4Plan: sumBy(rows, getter.pyQ4Plan),
  }

  const cy = {
    totalActual: sumBy(rows, getter.cyTotalActual),
    totalPlan: sumBy(rows, getter.cyTotalPlan),
    q1Actual: sumBy(rows, getter.cyQ1Actual),
    q2Actual: sumBy(rows, getter.cyQ2Actual),
    q3Actual: sumBy(rows, getter.cyQ3Actual),
    q4Actual: sumBy(rows, getter.cyQ4Actual),
    q1Plan: sumBy(rows, getter.cyQ1Plan),
    q2Plan: sumBy(rows, getter.cyQ2Plan),
    q3Plan: sumBy(rows, getter.cyQ3Plan),
    q4Plan: sumBy(rows, getter.cyQ4Plan),
  }

  return { py, cy }
}

const buildSobQuarterValues = (
  rows: FinData[],
  getters: {
    q1: (row: FinData) => number | null | undefined
    q2: (row: FinData) => number | null | undefined
    q3: (row: FinData) => number | null | undefined
    q4: (row: FinData) => number | null | undefined
  }
): SobQuarterValues => ({
  q1: averageBy(rows, getters.q1),
  q2: averageBy(rows, getters.q2),
  q3: averageBy(rows, getters.q3),
  q4: averageBy(rows, getters.q4),
})

const buildSalesQuarterValues = (quarters: {
  q1: number | null
  q2: number | null
  q3: number | null
  q4: number | null
}): SobQuarterValues => ({
  q1: quarters.q1,
  q2: quarters.q2,
  q3: quarters.q3,
  q4: quarters.q4,
})

const getIsNoPlan = (rows: FinData[]) =>
  rows.length > 0 && rows.every((row) => Boolean(row.IsNoPlan))

const getIsNoPlanContractual = (rows: FinData[]) =>
  rows.length > 0 && rows.every((row) => Boolean(row.IsNoPlanContractual))

export const buildSobPlanningTableModel = ({
  rows,
  currentFiscalYear,
  metricMode,
}: {
  rows: FinData[]
  currentFiscalYear: number
  metricMode: FinancialMetricMode
}): SobPlanningTableModel => {
  const { py, cy } = getTotals(rows, metricMode)
  const prevActuals = buildSobQuarterValues(rows, {
    q1: (row) => row.SobActualPYQ1,
    q2: (row) => row.SobActualPYQ2,
    q3: (row) => row.SobActualPYQ3,
    q4: (row) => row.SobActualPYQ4,
  })
  const prevPlan = buildSobQuarterValues(rows, {
    q1: (row) => row.SobPlanPYQ1,
    q2: (row) => row.SobPlanPYQ2,
    q3: (row) => row.SobPlanPYQ3,
    q4: (row) => row.SobPlanPYQ4,
  })
  const currentActuals = buildSobQuarterValues(rows, {
    q1: (row) => row.SobActualCYQ1,
    q2: (row) => row.SobActualCYQ2,
    q3: (row) => row.SobActualCYQ3,
    q4: (row) => row.SobActualCYQ4,
  })
  const currentPlan = buildSobQuarterValues(rows, {
    q1: (row) => row.SobPlanCYQ1,
    q2: (row) => row.SobPlanCYQ2,
    q3: (row) => row.SobPlanCYQ3,
    q4: (row) => row.SobPlanCYQ4,
  })
  const currentActualsSales = buildSalesQuarterValues({
    q1: cy.q1Actual,
    q2: cy.q2Actual,
    q3: cy.q3Actual,
    q4: cy.q4Actual,
  })
  const currentPlanSales = buildSalesQuarterValues({
    q1: cy.q1Plan,
    q2: cy.q2Plan,
    q3: cy.q3Plan,
    q4: cy.q4Plan,
  })

  const planningCells: PlanningCell[] = [
    buildPlanningCell(null, null, null, null),
    buildPlanningCell(py.q1Actual, py.q1Plan, null, null),
    buildPlanningCell(py.q2Actual, py.q2Plan, null, null),
    buildPlanningCell(py.q3Actual, py.q3Plan, null, null),
    buildPlanningCell(py.q4Actual, py.q4Plan, null, null),
    buildPlanningCell(py.totalActual, py.totalPlan, null, null),
    buildPlanningCell(cy.q1Actual, cy.q1Plan, py.q1Actual, py.q1Actual),
    buildPlanningCell(cy.q2Actual, cy.q2Plan, py.q2Actual, py.q2Actual),
    buildPlanningCell(cy.q3Actual, cy.q3Plan, py.q3Actual, py.q3Actual),
    buildPlanningCell(cy.q4Actual, cy.q4Plan, py.q4Actual, py.q4Actual),
    buildPlanningCell(cy.totalActual, cy.totalPlan, py.totalActual, py.totalActual),
  ]

  return {
    years: {
      prev: `FY${String(currentFiscalYear - 2).slice(-2)}`,
      current: `FY${String(currentFiscalYear - 1).slice(-2)}`,
      next: `FY${String(currentFiscalYear).slice(-2)}`,
    },
    isNoPlan: getIsNoPlan(rows),
    isNoPlanContractual: getIsNoPlanContractual(rows),
    planningCells,
    sob: {
      prevActualTotal: null,
      prevPlanTotal: null,
      prevActuals,
      prevPlan,
      prevActualsSales: buildSalesQuarterValues({
        q1: py.q1Actual,
        q2: py.q2Actual,
        q3: py.q3Actual,
        q4: py.q4Actual,
      }),
      prevPlanSales: buildSalesQuarterValues({
        q1: py.q1Plan,
        q2: py.q2Plan,
        q3: py.q3Plan,
        q4: py.q4Plan,
      }),
      currentActuals,
      currentPlan,
      currentActualsSales,
      currentPlanSales,
    },
  }
}
